import { Dropdown, MenuProps } from "antd";
import type { Category } from "api/config/chalice-types";
import { useMemo } from "react";
import { Post } from "store/posts/postConstants";

// Helper function to convert hex to RGBA
const hexToRgba = (hex: string, alpha: number): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");

  let r = 0,
    g = 0,
    b = 0;
  // 3 digits
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  }
  // 6 digits
  else if (hex.length === 6) {
    r = parseInt(hex[0] + hex[1], 16);
    g = parseInt(hex[2] + hex[3], 16);
    b = parseInt(hex[4] + hex[5], 16);
  } else {
    // Handle invalid hex format - return transparent grey or throw error
    console.warn("Invalid hex color provided to hexToRgba:", hex);
    return `rgba(128, 128, 128, ${alpha})`; // Default to grey
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper function to darken a hex color
const darkenColor = (hex: string, percent: number): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");

  let r = 0,
    g = 0,
    b = 0;
  // 3 digits
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  }
  // 6 digits
  else if (hex.length === 6) {
    r = parseInt(hex[0] + hex[1], 16);
    g = parseInt(hex[2] + hex[3], 16);
    b = parseInt(hex[4] + hex[5], 16);
  } else {
    console.warn("Invalid hex color provided to darkenColor:", hex);
    return "#808080"; // Default to grey
  }

  // Ensure percent is between 0 and 100
  percent = Math.max(0, Math.min(100, percent));

  r = Math.max(0, Math.floor(r * (1 - percent / 100)));
  g = Math.max(0, Math.floor(g * (1 - percent / 100)));
  b = Math.max(0, Math.floor(b * (1 - percent / 100)));

  // Convert back to hex
  const toHex = (c: number) => {
    const hexVal = c.toString(16);
    return hexVal.length === 1 ? "0" + hexVal : hexVal;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Helper function to lighten a hex color
const lightenColor = (hex: string, percent: number): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");

  let r = 0,
    g = 0,
    b = 0;
  // 3 digits
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  }
  // 6 digits
  else if (hex.length === 6) {
    r = parseInt(hex[0] + hex[1], 16);
    g = parseInt(hex[2] + hex[3], 16);
    b = parseInt(hex[4] + hex[5], 16);
  } else {
    console.warn("Invalid hex color provided to lightenColor:", hex);
    return "#808080"; // Default to grey
  }

  // Ensure percent is between 0 and 100
  percent = Math.max(0, Math.min(100, percent));

  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  // Convert back to hex
  const toHex = (c: number) => {
    const hexVal = c.toString(16);
    return hexVal.length === 1 ? "0" + hexVal : hexVal;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

interface PostCategoryTagProps {
  post: Post;
  categories: Category[];
  isLoadingCategories?: boolean;
  isUpdatingCategory?: boolean;
  disabled?: boolean;
  onUpdateCategory: (categoryId: string | null) => Promise<unknown>;
}

export const PostCategoryTag = ({
  post,
  categories,
  isLoadingCategories,
  isUpdatingCategory,
  disabled,
  onUpdateCategory,
}: PostCategoryTagProps) => {
  // --- Category Logic (Moved from PostCardHeader) ---
  const postCategory = useMemo(() => {
    if (!post.category_id) {
      return null;
    }
    // Ensure categories is an array before finding
    return (categories || []).find((cat) => cat.id === post.category_id);
  }, [post.category_id, categories]);

  const categoryName = postCategory?.name ?? "Uncategorized";
  const categoryColor = postCategory?.color ?? "#808080"; // Default gray

  const handleCategoryMenuClick: MenuProps["onClick"] = async (e) => {
    const newCategoryId = e.key === "uncategorized" ? null : e.key;
    try {
      await onUpdateCategory(newCategoryId);
    } catch (error) {
      console.error("Failed to update category:", error);
      // Error message handling assumed within onUpdateCategory
    }
  };

  const categoryMenuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [
      {
        key: "uncategorized",
        label: (
          <span style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                display: "inline-block",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#808080", // Default gray for uncategorized
                marginRight: "8px",
              }}
            />
            Uncategorized
          </span>
        ),
      },
    ];
    (categories || []).forEach((cat) => {
      if (cat.id) {
        items.push({
          key: cat.id,
          label: (
            <span style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: cat.color ?? "#808080", // Use category color or default
                  marginRight: "8px",
                }}
              />
              {cat.name}
            </span>
          ),
        });
      }
    });
    return items;
  }, [categories]);
  // --- End Category Logic ---

  const isDisabled = disabled || isLoadingCategories || isUpdatingCategory;

  // Dark mode detection (replace with your project's specific implementation if needed)
  const isDarkMode =
    typeof window !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const textColor = isDarkMode
    ? lightenColor(categoryColor, 50) // Lighten more for dark mode text
    : darkenColor(categoryColor, 40);
  const tagBackgroundColor = isDarkMode
    ? hexToRgba(categoryColor, 0.25) // Slightly more opaque for dark backgrounds
    : hexToRgba(categoryColor, 0.15);
  const borderColor = isDarkMode
    ? lightenColor(categoryColor, 20) // Lighter border for dark mode
    : categoryColor;

  return (
    <Dropdown
      menu={{
        items: categoryMenuItems,
        onClick: handleCategoryMenuClick,
        selectable: true,
        selectedKeys: [post.category_id ?? "uncategorized"],
      }}
      trigger={["click"]}
      disabled={isDisabled}
    >
      <span
        style={{
          backgroundColor: tagBackgroundColor,
          borderColor: borderColor,
          color: textColor,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: "4px", // Adjust as needed
          padding: "0px 7px", // Match Ant Tag padding
          overflow: "hidden", // Hide overflow
          textOverflow: "ellipsis", // Add ellipsis if text overflows
          whiteSpace: "nowrap", // Prevent wrapping
          maxWidth: "150px", // Set a max width for abbreviation
        }}
        title={categoryName} // Show full name on hover
        className={`inline-block cursor-pointer text-xs font-medium leading-5 align-middle ${
          isDisabled ? "cursor-not-allowed opacity-50" : "hover:opacity-80"
        }`}
      >
        {isLoadingCategories ? "Loading..." : categoryName}
      </span>
    </Dropdown>
  );
};
